import { type FC } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider, useAppearance } from '@segment/matcha/foundations/AppearanceContext'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import { SignupButtonText } from '@segment/matcha/recipes/SignupButton'

import DemoForm from '@segment/templates/Forms/DemoForm'

import type { AppearanceProps } from '@segment/matcha/types'

import heroPng from './assets/Hero.png'
import heroWebp from './assets/Hero.webp'

const copy = {
  overline: `Grow revenue with Twilio Segment`,
  heading: `Right message, right customer, every time`,
  description: `Use complete, real-time profiles and AI to anticipate customer needs and power personalized experiences that boost retention, improve loyalty, and grow customer lifetime value.`,
}

const Hero: FC<AppearanceProps> = ({ appearance: externalAppearance }) => {
  const appearance = useAppearance(externalAppearance)

  const afterContent = (
    <SignupButtonText size="medium" tag="a">
      Create a free account
      <ChevronRightIcon />
    </SignupButtonText>
  )

  return (
    <AppearanceProvider appearance={appearance}>
      <Wrapper>
        <StyledHero variant="2Col">
          <HeroRecipe.Copy>
            <CopyText.Overline>{copy.overline}</CopyText.Overline>
            <CopyText.Heading variant="homeH2" tag="h1">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="homePExtraLarge" tag="p" key={copy.description}>
              {copy.description}
            </CopyText.Description>
            <HeroRecipe.Form>
              <DemoFormContainer data-hook="demoFormContainer">
                <DemoForm
                  isProgressive={true}
                  customFormIdentifier="site-usecase-boostupsell-demo"
                  customTrackingType="site-usecase-boostupsell-demo"
                  after={afterContent}
                />
              </DemoFormContainer>
            </HeroRecipe.Form>
          </HeroRecipe.Copy>
          <HeroRecipe.Contents>
            <picture>
              <source type="image/webp" srcSet={`${heroWebp}`} />
              <HeroRecipe.Image
                width="1200"
                height="1134"
                src={heroPng}
                alt="Illustration: Customer Data Platform diagram"
                loading="eager"
              />
            </picture>
          </HeroRecipe.Contents>
        </StyledHero>
      </Wrapper>
    </AppearanceProvider>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.gray[100]};
`

const StyledHero = styled(HeroRecipeBase)`
  ${HeroRecipe.Wrapper} {
    gap: 50px;
  }
`

const DemoFormContainer = styled.div`
  margin-top: 50px;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-top: 0;
  }
`

export default Hero
